import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Select,
  Option,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  Stack,
  Divider,
  Alert,
  Chip,
  FormControl,
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionGroup,
  Tooltip,
  Badge,
} from '@mui/joy';
import {
  Plus as AddIcon,
  Trash as DeleteIcon,
  DotsSixVertical as DragIcon,
  PencilSimple as EditIcon,
  Sparkle as AIIcon,
  ArrowUp as ArrowUpward,
  ArrowDown as ArrowDownward,
  SlidersHorizontal as VariantIcon,
  Info as InfoIcon,
} from '@phosphor-icons/react';
import { SectionEditor } from './SectionEditor';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { OrganizationOverview } from '../../../types';

interface Section {
  type: string;
  variant?: string;
  data: Record<string, any>;
  order?: number;
}

interface SectionDefinition {
  type: string;
  label: string;
  category: string;
  description: string;
  variants: {
    [key: string]: {
      label: string;
      description: string;
      template: string;
      preview?: string;
    };
  };
  fields: Record<string, any>;
}

interface ContentTabV2Props {
  sections: Section[];
  onSectionsChange: (sections: Section[]) => void;
  aiSettings?: any;
  contentBrief?: string;
  siteId?: number;
  pageType?: string;
}

export const ContentTabV2: React.FC<ContentTabV2Props> = ({
  sections,
  onSectionsChange,
  aiSettings,
  contentBrief,
  siteId,
  pageType = 'generic',
}) => {
  const [editingSection, setEditingSection] = useState<number | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedSectionType, setSelectedSectionType] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<string>('');
  const [generatingContent, setGeneratingContent] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Fetch universal section manifest
  const { data: sectionManifest, isLoading: manifestLoading } = useQuery({
    queryKey: ['section-manifest-v2'],
    queryFn: async () => {
      const response = await api.get('/api/cms/sections/manifest/');
      return response.data;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Fetch organization branding for brand colors
  const { data: organizationBranding } = useQuery({
    queryKey: ['organization-overview'],
    queryFn: async () => {
      const response = await api.get<OrganizationOverview>('/api/marketing/organization-overview/');
      return response.data;
    },
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });

  // AI section recommendations
  const { data: recommendations } = useQuery({
    queryKey: ['section-recommendations', pageType],
    queryFn: async () => {
      const response = await api.post('/api/cms/ai/recommend-sections/', {
        page_type: pageType,
        keywords: aiSettings?.keywords || [],
        purpose: contentBrief || '',
      });
      return response.data;
    },
    enabled: !!pageType,
  });

  // Group sections by category
  const sectionsByCategory = React.useMemo(() => {
    if (!sectionManifest) return {};
    
    const grouped: Record<string, SectionDefinition[]> = {};
    const sections = (sectionManifest as any)?.sections || {};
    
    Object.entries(sections).forEach(([key, section]: [string, any]) => {
      const category = section.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push({ ...section, type: key });
    });
    
    return grouped;
  }, [sectionManifest]);

  // AI content generation mutation
  const generateContentMutation = useMutation({
    mutationFn: async (sectionIndex: number) => {
      const section = sections[sectionIndex];
      return api.post('/api/cms/ai/generate-section/', {
        section_type: section.type,
        variant: section.variant,
        current_data: section.data,
        ai_settings: aiSettings,
        content_brief: contentBrief,
      });
    },
    onSuccess: (data, sectionIndex) => {
      const newSections = [...sections];
      newSections[sectionIndex] = {
        ...newSections[sectionIndex],
        data: (data.data as any)?.content || data.data,
      };
      onSectionsChange(newSections);
    },
  });

  const handleAddSection = () => {
    if (!selectedSectionType) return;
    
    const sectionDef = (sectionManifest as any)?.sections?.[selectedSectionType];
    if (!sectionDef) return;
    
    // Select default variant if not selected
    const variant = selectedVariant || Object.keys(sectionDef.variants || {})[0] || 'default';
    
    const newSection: Section = {
      type: selectedSectionType,
      variant,
      data: {},
      order: sections.length + 1,
    };
    
    onSectionsChange([...sections, newSection]);
    setAddModalOpen(false);
    setSelectedSectionType('');
    setSelectedVariant('');
    
    // Start editing the new section
    setEditingSection(sections.length);
  };

  const handleDeleteSection = (index: number) => {
    const newSections = sections.filter((_, i) => i !== index);
    // Reorder remaining sections
    newSections.forEach((section, i) => {
      section.order = i + 1;
    });
    onSectionsChange(newSections);
  };

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    const newSections = [...sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSections.length) return;
    
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    // Update order
    newSections.forEach((section, i) => {
      section.order = i + 1;
    });
    
    onSectionsChange(newSections);
  };

  const handleSectionChange = (index: number, newData: any) => {
    const newSections = [...sections];
    newSections[index] = {
      ...newSections[index],
      data: newData,
    };
    onSectionsChange(newSections);
  };

  const handleVariantChange = (index: number, newVariant: string) => {
    const newSections = [...sections];
    newSections[index] = {
      ...newSections[index],
      variant: newVariant,
    };
    onSectionsChange(newSections);
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, any> = {
      hero: '🏆',
      content: '📄',
      features: '✨',
      social_proof: '⭐',
      media: '🖼️',
      cta: '📢',
      business: '💼',
      contact: '📞',
      informational: 'ℹ️',
    };
    return icons[category] || '📦';
  };

  const isRecommended = (sectionType: string) => {
    return (recommendations as any)?.recommendations?.some(
      (rec: any) => rec.type === sectionType
    );
  };

  if (manifestLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading section library...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Section List */}
      {sections.length === 0 ? (
        <Alert color="primary" sx={{ mb: 3 }}>
          <Typography level="body-md">
            No sections added yet. Click "Add Section" to start building your page.
          </Typography>
          {(recommendations as any)?.recommendations && (
            <Box sx={{ mt: 2 }}>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                AI Recommendations for {pageType} page:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(recommendations as any).recommendations.slice(0, 5).map((rec: any) => (
                  <Chip
                    key={rec.type}
                    size="sm"
                    color="primary"
                    variant="soft"
                  >
                    {rec.type}
                  </Chip>
                ))}
              </Stack>
            </Box>
          )}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {sections.map((section, index) => {
            const sectionDef = (sectionManifest as any)?.sections?.[section.type];
            const variantDef = sectionDef?.variants?.[section.variant || 'default'];
            
            return (
              <Card key={index}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton size="sm" sx={{ mr: 1 }}>
                      <DragIcon />
                    </IconButton>
                    
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography level="title-md">
                          {sectionDef?.label || section.type}
                        </Typography>
                        {section.variant && (
                          <Chip size="sm" variant="outlined" startDecorator={<VariantIcon />}>
                            {variantDef?.label || section.variant}
                          </Chip>
                        )}
                      </Stack>
                      {sectionDef?.description && (
                        <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.secondary' }}>
                          {sectionDef.description}
                        </Typography>
                      )}
                    </Box>
                    
                    <Stack direction="row" spacing={1}>
                      {/* Variant Selector */}
                      {sectionDef?.variants && Object.keys(sectionDef.variants).length > 1 && (
                        <Select
                          size="sm"
                          value={section.variant || Object.keys(sectionDef.variants)[0]}
                          onChange={(_, value) => value && handleVariantChange(index, value)}
                        >
                          {Object.entries(sectionDef.variants).map(([key, variant]: [string, any]) => (
                            <Option key={key} value={key}>
                              {variant.label}
                            </Option>
                          ))}
                        </Select>
                      )}
                      
                      <Tooltip title="Generate AI Content">
                        <IconButton
                          size="sm"
                          onClick={() => generateContentMutation.mutate(index)}
                          loading={generatingContent}
                        >
                          <AIIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <IconButton
                        size="sm"
                        onClick={() => setEditingSection(editingSection === index ? null : index)}
                      >
                        <EditIcon />
                      </IconButton>
                      
                      <IconButton
                        size="sm"
                        onClick={() => handleMoveSection(index, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUpward />
                      </IconButton>
                      
                      <IconButton
                        size="sm"
                        onClick={() => handleMoveSection(index, 'down')}
                        disabled={index === sections.length - 1}
                      >
                        <ArrowDownward />
                      </IconButton>
                      
                      <IconButton
                        size="sm"
                        color="danger"
                        onClick={() => handleDeleteSection(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                  
                  {/* Inline section preview - can be expanded later */}
                  {editingSection === index && sectionDef && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        Section editing will open in a modal. Click the edit button to modify content.
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
      
      {/* Add Section Button */}
      <Button
        variant="outlined"
        color="primary"
        startDecorator={<AddIcon />}
        onClick={() => setAddModalOpen(true)}
        sx={{ mt: 3 }}
        fullWidth
      >
        Add Section
      </Button>
      
      {/* Add Section Modal */}
      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)}>
        <ModalDialog size="lg" sx={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography level="title-lg">Add Section</Typography>
              {!!recommendations && (
                <Chip size="sm" color="success" variant="soft">
                  AI Recommendations Available
                </Chip>
              )}
            </Stack>
          </DialogTitle>
          
          <DialogContent>
            {/* Section Categories */}
            <AccordionGroup>
              {Object.entries(sectionsByCategory).map(([category, categorySections]) => {
                const categoryInfo = (sectionManifest as any)?.categories?.[category];
                
                return (
                  <Accordion
                    key={category}
                    expanded={expandedCategory === category}
                    onChange={(_, expanded) => setExpandedCategory(expanded ? category : null)}
                  >
                    <AccordionSummary>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontSize="lg">{getCategoryIcon(category)}</Typography>
                        <Typography level="title-md">{categoryInfo?.label || category}</Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          ({categorySections.length} sections)
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {categorySections.map((section) => {
                          const recommended = isRecommended(section.type);
                          
                          return (
                            <Card
                              key={section.type}
                              variant={selectedSectionType === section.type ? 'soft' : 'outlined'}
                              color={selectedSectionType === section.type ? 'primary' : 'neutral'}
                              sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  boxShadow: 'md',
                                },
                              }}
                              onClick={() => setSelectedSectionType(section.type)}
                            >
                              <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                  <Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography level="title-md">{section.label}</Typography>
                                      {recommended && (
                                        <Badge color="success" variant="soft" size="sm">
                                          AI Recommended
                                        </Badge>
                                      )}
                                    </Stack>
                                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                                      {section.description}
                                    </Typography>
                                    
                                    {/* Variant Preview */}
                                    {selectedSectionType === section.type && section.variants && (
                                      <Box sx={{ mt: 2 }}>
                                        <FormControl>
                                          <FormLabel>Choose Variant</FormLabel>
                                          <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {Object.entries(section.variants).map(([key, variant]: [string, any]) => (
                                              <Chip
                                                key={key}
                                                onClick={() => setSelectedVariant(key)}
                                                color={selectedVariant === key ? 'primary' : 'neutral'}
                                                variant={selectedVariant === key ? 'solid' : 'outlined'}
                                              >
                                                {variant.label}
                                              </Chip>
                                            ))}
                                          </Stack>
                                        </FormControl>
                                      </Box>
                                    )}
                                  </Box>
                                  
                                  {recommended && (
                                    <Tooltip title="AI recommends this section for your page type">
                                      <InfoIcon color="var(--joy-palette-success-500)" />
                                    </Tooltip>
                                  )}
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </AccordionGroup>
            
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Button
                onClick={handleAddSection}
                disabled={!selectedSectionType}
                fullWidth
              >
                Add Selected Section
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setAddModalOpen(false);
                  setSelectedSectionType('');
                  setSelectedVariant('');
                }}
                fullWidth
              >
                Cancel
              </Button>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
      
      {/* Section Editor Modal */}
      {editingSection !== null && sections[editingSection] && (
        <SectionEditor
          section={sections[editingSection]}
          sectionConfig={(sectionManifest as any)?.sections?.[sections[editingSection].type]}
          onSave={(data) => {
            handleSectionChange(editingSection, data);
            setEditingSection(null);
          }}
          onClose={() => setEditingSection(null)}
          aiSettings={aiSettings}
          contentBrief={contentBrief}
          siteId={siteId}
          brandColors={organizationBranding ? {
            primary: organizationBranding.brand_color_1 ? `#${organizationBranding.brand_color_1}` : undefined,
            secondary: organizationBranding.brand_color_2 ? `#${organizationBranding.brand_color_2}` : undefined,
            tertiary: organizationBranding.brand_color_3 ? `#${organizationBranding.brand_color_3}` : undefined,
            quaternary: organizationBranding.brand_color_4 ? `#${organizationBranding.brand_color_4}` : undefined,
            quinary: organizationBranding.brand_color_5 ? `#${organizationBranding.brand_color_5}` : undefined,
          } : undefined}
        />
      )}
    </Box>
  );
};